import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import * as yup from 'yup'
import DefaultEditor from 'react-simple-wysiwyg'
import { all_routes } from '../../router/all_routes'
import InstructorSidebar from '../common/instructorSidebar'
import {
  additionalInformationFieldsConfig,
  contentFieldsConfig,
  CourseAdditionalInformationFormValues,
  courseAdditionalInformationSchema,
  courseBasicInformationSchema,
  CourseContentFormValues,
  courseContentSchema,
  CourseCreateFormValues,
  courseInformationFieldsConfig,
  CoursePricingFormValues,
  coursePricingSchema,
  initialFullFormValues,
  initialValuesStep1,
  initialValuesStep2,
  initialValuesStep3,
  initialValuesStep4,
  pricingFieldsConfig,
  transformCoursePayload,
  courseQuestionInformationFieldsConfig,
  CourseQuestionInformationFormValues,
  courseQuestionInformationSchema,
  initialValuesStep2b,
  parseCertificateMaterials,
} from '../../../config/fields/course'
import {
  FieldArrayWithId,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Select, Radio, TimePicker } from 'antd'
import dayjs from 'dayjs'
import AddQuestionForm from '../cbt/components/QuestionForm'
import { normalizeLeadingInteger } from '../../../utils/numberNormalizer'
import DynamicSelectFetcher from '../../../core/common/DynamicSelectFetcher'
import { Modal } from 'react-bootstrap'
import { useMutation } from '@tanstack/react-query'
import { postCourse } from '../../../api/courses'
import { useNotify } from '../../../provider/NotificationProvider'
import SelectIcon from '../common/SelectIcon'
import CertificateMaterialsInput from '../common/CertificateMaterialsInput'
import { useCourseList } from './hooks/useCourses'
import DurationPicker from '../../../core/common/DurationPicker'

const CourseCreate = () => {
  const route = all_routes
  const notify = useNotify()
  const navigate = useNavigate()

  const [currentStep, setCurrentStep] = useState(1)
  const [allFormData, setAllFormData] = useState<CourseCreateFormValues>(
    initialFullFormValues,
  )

  const [showLessonModal, setShowLessonModal] = useState(false)
  const [currentTopicIndex, setCurrentTopicIndex] = useState<number | null>(
    null,
  )
  const [currentLessonIndex, setCurrentLessonIndex] = useState<number | null>(
    null,
  )

  const [lessonName, setLessonName] = useState('')
  const [lessonVideoLink, setLessonVideoLink] = useState('')
  const [lessonDescription, setLessonDescription] = useState('')
  const [lessonDuration, setLessonDuration] = useState('')
  const [isPremium, setIsPremium] = useState(false)
  const [lessonErrors, setLessonErrors] = useState<
    Partial<Record<keyof any, string>>
  >({})

  const [studiedItems, setStudiedItems] = useState<string[]>([''])
  const [crossSellItems, setCrossSellItems] = useState<
    Array<{
      cross_course_id: number | null
      note: string
      price: number | null
    }>
  >([])
  const [benefitsItems, setBenefitsItems] = useState<
    Array<{ icon: string; name: string }>
  >([])
  const [includedCourseType, setIncludedCourseType] = useState('all')

  const { data, isLoading } = useCourseList({
    initialLimit: 100,
    type: 'all',
  })

  const { data: includedCourseData, isLoading: isIncludedCourseLoading } =
    useCourseList({
      initialLimit: 100,
      type: includedCourseType,
    })

  const optionsCourse = data?.data?.map((course) => ({
    label: course.title,
    value: course.id,
  }))

  const optionsIncludedCourse = includedCourseData?.data?.map((course) => ({
    label: course.title,
    value: course.id,
  }))

  const buildIncludedCourseOptions = (
    selectedIds: Array<number | string> | number | string | null | undefined = [],
  ) => {
    const normalizedIds = Array.isArray(selectedIds)
      ? selectedIds
      : selectedIds !== null && selectedIds !== undefined
      ? [selectedIds]
      : []
    const selectedValues = new Set(normalizedIds.map((id) => String(id)))
    const filtered = (optionsIncludedCourse || []).filter(
      (opt) => !selectedValues.has(String(opt.value)),
    )
    return filtered
  }

  type CourseBasicInformationFormValues = yup.InferType<typeof courseBasicInformationSchema>

  const {
    register: registerStep1,
    handleSubmit: handleSubmitStep1,
    formState: { errors: errorsStep1 },
    setValue: setValueStep1,
    watch: watchStep1,
    trigger: triggerStep1,
    getValues: getValuesStep1,
  } = useForm<CourseBasicInformationFormValues>({
    resolver: yupResolver(courseBasicInformationSchema as any),
    defaultValues: initialValuesStep1,
    mode: 'onBlur', // Validate on blur
  })

  const {
    register: registerStep2,
    handleSubmit: handleSubmitStep2,
    formState: { errors: errorsStep2 },
    setValue: setValueStep2,
    watch: watchStep2,
    trigger: triggerStep2,
    control,
    getValues: getValuesStep2,
  } = useForm<CourseContentFormValues>({
    resolver: yupResolver(courseContentSchema),
    defaultValues: initialValuesStep2,
    mode: 'onBlur', // Validate on blur
  })

  const {
    fields: topicFields,
    append: appendTopic,
    remove: removeTopic,
  } = useFieldArray<CourseContentFormValues, 'topic', 'id'>({
    control,
    name: 'topic',
  })

  const {
    register: registerStep2b,
    handleSubmit: handleSubmitStep2b,
    formState: { errors: errorsStep2b },
    setValue: setValueStep2b,
    watch: watchStep2b,
    trigger: triggerStep2b,
    getValues: getValuesStep2b,
  } = useForm<CourseQuestionInformationFormValues>({
    resolver: yupResolver(courseQuestionInformationSchema),
    defaultValues: initialValuesStep2b,
    mode: 'onBlur',
  })

  const isQuestionEnabled = watchStep2b('has_questions') === 'yes'

  type CertificateRequiredField =
    | 'certificate_materials'
    | 'certificate_location'
  const requiredCertificateFieldKeys = [
    'certificate_materials',
    'certificate_location',
  ] as CertificateRequiredField[]

  const {
    register: registerStep3,
    handleSubmit: handleSubmitStep3,
    formState: { errors: errorsStep3 },
    setValue: setValueStep3,
    watch: watchStep3,
    trigger: triggerStep3,
    getValues: getValuesStep3,
    setError: setErrorStep3,
    clearErrors: clearErrorsStep3,
  } = useForm<CourseAdditionalInformationFormValues>({
    resolver: yupResolver(courseAdditionalInformationSchema),
    defaultValues: initialValuesStep3,
    mode: 'onBlur', // Validate on blur
  })

  const {
    register: registerStep4,
    handleSubmit: handleSubmitStep4,
    formState: { errors: errorsStep4 },
    setValue: setValueStep4,
    watch: watchStep4,
    trigger: triggerStep4,
    getValues: getValuesStep4,
  } = useForm<CoursePricingFormValues>({
    resolver: yupResolver(coursePricingSchema),
    defaultValues: initialValuesStep4,
    mode: 'onBlur', // Validate on blur
  })

  const createMutation = useMutation({
    mutationFn: (data: any) => postCourse(data, true),
    onSuccess: () => {
      notify.success({
        message: 'Create berhasil',
        description: 'Data course berhasil ditambahkan',
      })
      navigate(route.adminCourses)
    },
    onError: (err: any) => {
      let errorMessage = 'Terjadi kesalahan.'

      // Extract error message from API response
      if (err?.response?.data?.message) {
        errorMessage = err.response.data.message
      } else if (err?.response?.data?.error) {
        errorMessage = err.response.data.error
      } else if (err?.response?.data) {
        // Handle validation errors like {"slug":["The slug has already been taken."]}
        if (
          typeof err.response.data === 'object' &&
          !Array.isArray(err.response.data)
        ) {
          const errorMessages: string[] = []

          Object.entries(err.response.data).forEach(([field, messages]) => {
            if (Array.isArray(messages)) {
              messages.forEach((message: string) => {
                errorMessages.push(`${field}: ${message}`)
              })
            } else if (typeof messages === 'string') {
              errorMessages.push(`${field}: ${messages}`)
            }
          })

          errorMessage =
            errorMessages.length > 0
              ? errorMessages.join('\n')
              : JSON.stringify(err.response.data)
        } else if (typeof err.response.data === 'string') {
          errorMessage = err.response.data
        }
      } else if (err?.message) {
        errorMessage = err.message
      }

      notify.error({
        message: 'Create course gagal',
        description: errorMessage,
      })
    },
  })

  const watchedThumbnail: any = watchStep1('thumbnail')

  // Handler to move to the next step
  const handleNextStep = async () => {
    let isValid = false
    let currentStepData: any = null

    if (currentStep === 1) {
      isValid = await triggerStep1()
      if (isValid) {
        currentStepData = getValuesStep1()
        setAllFormData((prev) => ({ ...prev, ...currentStepData }))
      }
    } else if (currentStep === 2) {
      isValid = await triggerStep2()
      if (isValid) {
        currentStepData = getValuesStep2()
        setAllFormData((prev) => ({ ...prev, ...currentStepData }))
      }
    } else if (currentStep === 3) {
      isValid = await triggerStep2b()
      if (isValid) {
        currentStepData = getValuesStep2b()
        setAllFormData((prev) => ({ ...prev, ...currentStepData }))
      }
    } else if (currentStep === 5) {
      isValid = await triggerStep3()
      if (isValid) {
        currentStepData = getValuesStep3()
        setAllFormData((prev) => ({ ...prev, ...currentStepData }))
      }
    }

    if (isValid) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleSelectChange = (
    fieldName:
      | keyof CourseBasicInformationFormValues
      | keyof CoursePricingFormValues,
    value: any,
    setFormValue: any,
  ) => {
    setFormValue(fieldName, value)
  }

  const handleEditorChange = (
    fieldName: keyof CourseBasicInformationFormValues,
    value: string,
    setFormValue: any,
  ) => {
    setFormValue(fieldName, value, { shouldValidate: true, shouldDirty: true })
  }

  const handlePrevStep = () => {
    setCurrentStep(currentStep - 1)
  }

  const handleAddTopic = () => {
    appendTopic({ name: '', lesson: [] })
  }

  const handleRemoveTopic = (index: number) => {
    if (
      window.confirm(
        'Are you sure you want to remove this topic and all its lessons?',
      )
    ) {
      removeTopic(index)
    }
  }

  // --- Lesson Modal Management (Manual State & Validation) ---
  const openLessonModal = (
    topicIndex: number,
    lessonIndex: number | null = null,
  ) => {
    setCurrentTopicIndex(topicIndex)
    setCurrentLessonIndex(lessonIndex)
    setLessonErrors({}) // Clear previous errors

    if (lessonIndex !== null) {
      // Populate form for editing existing lesson
      const lessonData = topicFields?.[topicIndex]?.lesson?.[lessonIndex] as any
      setLessonName(lessonData.name)
      setLessonVideoLink(lessonData.video_link)
      setLessonDuration(lessonData.duration || '')
      setLessonDescription(lessonData.description || '') // Handle undefined
      setIsPremium(lessonData.is_premium)
    } else {
      // Reset for new lesson
      setLessonName('')
      setLessonVideoLink('')
      setLessonDuration('')
      setLessonDescription('')
      setIsPremium(false)
    }

    setShowLessonModal(true) // Show the react-bootstrap Modal
  }

  const closeLessonModal = () => {
    setShowLessonModal(false) // Hide the react-bootstrap Modal
    setCurrentTopicIndex(null)
    setCurrentLessonIndex(null)
    // Reset all lesson modal states
    setLessonName('')
    setLessonVideoLink('')
    setLessonDuration('')
    setLessonDescription('')
    setIsPremium(false)
    setLessonErrors({})
  }

  const handleLessonSubmit = async (event: React.FormEvent) => {
    event.preventDefault() // Prevent default form submission

    if (currentTopicIndex === null) return

    const lessonData: any = {
      name: lessonName,
      video_link: lessonVideoLink,
      duration: lessonDuration,
      description: lessonDescription,
      is_premium: isPremium,
    }

    // Ambil schema lesson dari field config
    const lessonSchema = (contentFieldsConfig.topic.schema as any).innerType
      .fields.lesson as any // ambil schema dari topic

    try {
      // Validasi satu lesson saja (bukan array), jadi ambil innerType dari lesson
      await lessonSchema.innerType.validate(lessonData, { abortEarly: false })

      setLessonErrors({}) // Clear errors on successful validation

      const currentTopics = getValuesStep2('topic')
      const updatedTopics = [...currentTopics]

      if (currentLessonIndex !== null) {
        // Editing existing lesson
        updatedTopics[currentTopicIndex].lesson[currentLessonIndex] = lessonData
      } else {
        // Adding new lesson
        updatedTopics[currentTopicIndex].lesson.push(lessonData)
      }

      // Update the main form's topic array using setValue
      setValueStep2('topic', updatedTopics, {
        shouldValidate: true,
        shouldDirty: true,
      })
      closeLessonModal() // Close modal on successful submission
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const newErrors: Partial<Record<keyof typeof lessonData, string>> = {}
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path as keyof typeof lessonData] = error.message
          }
        })
        setLessonErrors(newErrors)
        console.log('Lesson Validation Errors:', newErrors)
      } else {
        console.error('Unexpected error during lesson submission:', err)
      }
    }
  }

  useEffect(() => {
    if (studiedItems.length > 0) {
      setValueStep1('studied', studiedItems, {
        shouldValidate: true,
        shouldDirty: true,
      })
    } else {
      setValueStep1('studied', null, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
  }, [studiedItems, setValueStep1])

  useEffect(() => {
    if (crossSellItems.length > 0) {
      setValueStep4('cross_sell', crossSellItems, {
        shouldValidate: true,
        shouldDirty: true,
      })
    } else {
      setValueStep4('cross_sell', null, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
  }, [crossSellItems, setValueStep4])

  const addNewStudiedItem = () => {
    setStudiedItems([...studiedItems, ''])
  }

  const updateStudiedItem = (index: number, value: number | string) => {
    const newItems: any = [...studiedItems]
    newItems[index] = value
    setStudiedItems(newItems)
  }

  const removeStudiedItem = (index: number) => {
    setStudiedItems(studiedItems.filter((_, i) => i !== index))
  }

  const addNewCrossSellItem = () => {
    setCrossSellItems([
      ...crossSellItems,
      { cross_course_id: null, note: '', price: null },
    ])
  }

  const updateCrossSellItem = (
    index: number,
    field: 'cross_course_id' | 'note' | 'price',
    value: number | string,
  ) => {
    const newItems: any = [...crossSellItems]
    newItems[index][field] = value
    setCrossSellItems(newItems)
  }

  const removeCrossSellItem = (index: number) => {
    setCrossSellItems(crossSellItems.filter((_, i) => i !== index))
  }

  useEffect(() => {
    if (benefitsItems.length > 0) {
      setValueStep3('benefits', benefitsItems, {
        shouldValidate: true,
        shouldDirty: true,
      })
    } else {
      setValueStep3('benefits', null, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
  }, [benefitsItems, setValueStep3])

  useEffect(() => {
    if (!isQuestionEnabled) {
      setValueStep2b('questions', [], {
        shouldValidate: true,
        shouldDirty: true,
      })
      setValueStep2b('duration', null as any, {
        shouldValidate: false,
        shouldDirty: true,
      })
      setValueStep2b('min_passing_score', '' as any, {
        shouldValidate: false,
        shouldDirty: true,
      })
      setValueStep2b('max_attempt', '' as any, {
        shouldValidate: false,
        shouldDirty: true,
      })
    }
  }, [isQuestionEnabled, setValueStep2b])

  const validateCertificateFields = () => {
    clearErrorsStep3(requiredCertificateFieldKeys)

    const values = getValuesStep3()
    let isValid = true

    if (!isQuestionEnabled) return isValid

    const requiredTextFields = [
      'certificate_location',
    ] as CertificateRequiredField[]

    requiredTextFields.forEach((field) => {
      if (!String(values[field] || '').trim()) {
        setErrorStep3(field, {
          type: 'manual',
          message: 'Field ini wajib diisi jika Quiz CBT aktif',
        })
        isValid = false
      }
    })

    const parsedMaterials = parseCertificateMaterials(values.certificate_materials)
    const materials = parsedMaterials.length
      ? parsedMaterials
      : studiedItems
          .map((topic) => ({ topic, jpl: '', speaker: '' }))
          .filter((item) => item.topic.trim())
    const hasCompleteMaterial = materials.some(
      (item) => item.topic,
    )

    if (!hasCompleteMaterial) {
      setErrorStep3('certificate_materials', {
        type: 'manual',
        message: 'Minimal satu Materi Sertifikat wajib diisi jika Quiz CBT aktif',
      })
      isValid = false
    }

    return isValid
  }

  const addNewBenefitItem = () => {
    setBenefitsItems([...benefitsItems, { icon: '', name: '' }])
  }

  const updateBenefitItem = (
    index: number,
    field: 'name' | 'icon',
    value: number | string,
  ) => {
    const newItems: any = [...benefitsItems]
    newItems[index][field] = value
    setBenefitsItems(newItems)
  }

  const removeBenefitItem = (index: number) => {
    setBenefitsItems(benefitsItems.filter((_, i) => i !== index))
  }

  const onSubmitFinal: SubmitHandler<any> = async () => {
    const isStep4Valid = await triggerStep4()

    if (!isStep4Valid) {
      return
    }

    // Update allFormData with the last step's data before constructing payload
    const finalStep4Data = getValuesStep4()

    const finalAllFormData = { ...allFormData, ...finalStep4Data }
    const dataFormatted = transformCoursePayload(finalAllFormData)

    createMutation.mutate(dataFormatted)
  }

  return (
    <>
      <div className="content">
        <div className="container">
          <div className="row">
            <InstructorSidebar />
            <div className="col-lg-9 mx-auto">
              <div className="add-course-item">
                <div className="wizard">
                  <ul className="form-wizard-steps" id="progressbar2">
                    {[1, 2, 3, 4, 5, 6].map((stepNum) => (
                      <li
                        key={stepNum}
                        className={
                          currentStep === stepNum
                            ? 'progress-active'
                            : currentStep > stepNum
                              ? 'progress-activated'
                              : ''
                        }
                      >
                        <div className="profile-step">
                          <span className="dot-active mb-2">
                            <span className="number">0{stepNum}</span>
                            <span className="tickmark">
                              <i className="fa-solid fa-check" />
                            </span>
                          </span>
                          <div className="step-section">
                            <p>
                              {stepNum === 1 && 'Course Information'}
                              {stepNum === 2 && 'Content'}
                              {stepNum === 3 && 'Question & Answer'}
                              {stepNum === 4 && 'Sertifikat'}
                              {stepNum === 5 && 'Additional Information'}
                              {stepNum === 6 && 'Pricing'}
                            </p>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="initialization-form-set">
                  {currentStep === 1 && (
                    <form
                      onSubmit={handleSubmitStep1(handleNextStep)}
                      className="form-inner wizard-form-card"
                      id="first"
                    >
                      <div className="title">
                        <h5>Basic Information</h5>
                      </div>
                      <div className="row">
                        {Object.entries(courseInformationFieldsConfig).map(
                          ([key, fieldConfig]: any) => {
                            const isDynamicSelect =
                              fieldConfig.type === 'select' &&
                              fieldConfig.isSearchableAndInfinite
                            const isStaticSelect =
                              fieldConfig.type === 'select' &&
                              !fieldConfig.isSearchableAndInfinite

                            const error = errorsStep1[key]

                            const inputClassName = `form-control ${error ? 'is-invalid' : ''
                              }`
                            const isFullWidth = [
                              'title',
                              'short_description',
                              'description',
                              'studied',
                              'thumbnail',
                              'included_course_ids',
                              'is_favorite',
                            ].includes(key)

                            const selectedIncludedIds = (watchStep1(key) || []) as Array<
                              number | string
                            >
                            const includedOptions = buildIncludedCourseOptions(
                              selectedIncludedIds,
                            )

                            return (
                              <div
                                className={`col-md-${isFullWidth ? '12' : '6'}`}
                                key={key}
                              >
                                <div className="input-block mb-3">
                                  {fieldConfig.type !== 'checkbox' && (
                                    <label className="form-label">
                                      {fieldConfig.label}
                                      {fieldConfig.required && (
                                        <span className="text-danger ms-1">
                                          *
                                        </span>
                                      )}
                                    </label>
                                  )}
                                  {(fieldConfig.type === 'text' ||
                                    fieldConfig.type === 'number' ||
                                    fieldConfig.type === 'url') && (
                                      <input
                                        type={
                                          fieldConfig.type === 'url'
                                            ? 'text'
                                            : fieldConfig.type
                                        }
                                        {...registerStep1(key, {
                                          valueAsNumber:
                                            fieldConfig.type === 'number',
                                        })}
                                        className={inputClassName}
                                        placeholder={fieldConfig.placeholder}
                                      />
                                    )}
                                  {fieldConfig.type === 'textarea' && (
                                    <textarea
                                      {...registerStep1(key)}
                                      className={inputClassName}
                                      placeholder={fieldConfig.placeholder}
                                    ></textarea>
                                  )}
                                  {isDynamicSelect && (
                                    <DynamicSelectFetcher
                                      {...registerStep1(key)}
                                      fieldConfig={fieldConfig}
                                      value={watchStep1(key)}
                                      onChange={(selectedOption: any) => {
                                        handleSelectChange(
                                          key,
                                          selectedOption,
                                          setValueStep1,
                                        )
                                      }}
                                      placeholder={fieldConfig.placeholder}
                                    />
                                  )}
                                  {isStaticSelect && (
                                    <Select
                                      {...registerStep1(key)}
                                      options={fieldConfig?.options || []}
                                      className="select d-flex"
                                      style={{ width: '100%' }}
                                      placeholder={fieldConfig.placeholder}
                                      value={
                                        fieldConfig?.options?.find(
                                          (opt: any) =>
                                            opt.value === watchStep1(key),
                                        ) || null
                                      }
                                      onChange={(selectedOption: any) => {
                                        handleSelectChange(
                                          key,
                                          selectedOption,
                                          setValueStep1,
                                        )
                                      }}
                                    />
                                  )}
                                  {fieldConfig.type === 'multi-select' && (
                                    <>
                                      <div className="mb-2">
                                        <Select
                                          className="select d-flex"
                                          style={{ width: '100%' }}
                                          placeholder="Filter tipe course"
                                          value={includedCourseType}
                                          onChange={(value) =>
                                            setIncludedCourseType(value)
                                          }
                                          options={[
                                            { label: 'Semua', value: 'all' },
                                            { label: 'Course', value: 'Course' },
                                            { label: 'CBT', value: 'CBT' },
                                            {
                                              label: 'Live Teaching',
                                              value: 'Live_Teaching',
                                            },
                                          ]}
                                        />
                                      </div>
                                      <Select
                                        {...registerStep1(key)}
                                        mode="multiple"
                                        options={includedOptions}
                                        className="select d-flex"
                                        style={{ width: '100%' }}
                                        placeholder={fieldConfig.placeholder}
                                        showSearch
                                        optionFilterProp="label"
                                        value={watchStep1(key) || []}
                                        onChange={(selectedOption: any) =>
                                          handleSelectChange(
                                            key,
                                            selectedOption,
                                            setValueStep1,
                                          )
                                        }
                                        loading={isIncludedCourseLoading}
                                        allowClear
                                      />
                                    </>
                                  )}
                                  {fieldConfig.type === 'richtext' && (
                                    <div className="summernote">
                                      <DefaultEditor
                                        value={
                                          (watchStep1(key) as string) || ''
                                        }
                                        onChange={(e) =>
                                          handleEditorChange(
                                            key,
                                            e.target.value,
                                            setValueStep1,
                                          )
                                        }
                                      />
                                    </div>
                                  )}
                                  {fieldConfig.type === 'file' && (
                                    <div className="row align-items-center">
                                      <div className="col-md-10">
                                        <input
                                          type="text"
                                          className={inputClassName}
                                          placeholder="No File Selected"
                                          readOnly
                                          value={
                                            watchedThumbnail?.[0]?.name || ''
                                          }
                                        />
                                      </div>
                                      <div className="col-md-2 d-grid">
                                        <label
                                          htmlFor={`${key}-upload`}
                                          className="file-upload-btn text-center"
                                        >
                                          Upload File
                                        </label>
                                        <input
                                          type="file"
                                          id={`${key}-upload`}
                                          {...registerStep1(key)}
                                          className="d-none" // Hide default file input
                                        />
                                      </div>
                                    </div>
                                  )}
                                  {fieldConfig.type === 'array' && (
                                    <>
                                      {studiedItems.length === 0 && (
                                        <div className="alert alert-info text-center">
                                          No Data added.
                                        </div>
                                      )}
                                      {studiedItems.map((item, index) => {
                                        return (
                                          <div
                                            key={index}
                                            className="d-flex align-items-center mb-2"
                                          >
                                            <div className="flex-grow-1 me-2">
                                              <input
                                                type="text"
                                                className="form-control w-100"
                                                placeholder="Apa yang akan dipelajari"
                                                value={item}
                                                onChange={(e) =>
                                                  updateStudiedItem(
                                                    index,
                                                    e.target.value,
                                                  )
                                                }
                                              />
                                            </div>
                                            <Link
                                              to="#"
                                              className="link-trash delete-btn1"
                                              onClick={() =>
                                                removeStudiedItem(index)
                                              }
                                            >
                                              <i className="isax isax-trash fs-16" />
                                            </Link>
                                          </div>
                                        )
                                      })}

                                      <div className="d-flex align-items-center justify-content-end">
                                        <Link
                                          to="#"
                                          className="d-flex align-items-center add-new-topic"
                                          id="add-new-topic-btn"
                                          onClick={addNewStudiedItem}
                                        >
                                          <i className="isax isax-add me-1" />{' '}
                                          Add New
                                        </Link>
                                        <div></div>
                                      </div>
                                    </>
                                  )}
                                  {fieldConfig.type === 'checkbox' && (
                                    <div className="d-flex align-items-center gap-3 p-3 rounded-3 border bg-light mt-2 mb-2">
                                      <div className="form-check form-switch form-check-md m-0 p-0 d-flex align-items-center">
                                        <input
                                          type="checkbox"
                                          id={key}
                                          checked={Boolean(watchStep1(key))}
                                          onChange={(e) =>
                                            setValueStep1(key, e.target.checked, {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            })
                                          }
                                          className="form-check-input ms-0 cursor-pointer"
                                          style={{ width: '46px', height: '24px', cursor: 'pointer' }}
                                        />
                                        <label
                                          className="form-check-label fw-bold text-dark cursor-pointer ms-3 mb-0"
                                          htmlFor={key}
                                          style={{ cursor: 'pointer', fontSize: '14px' }}
                                        >
                                          {fieldConfig.label}
                                        </label>
                                      </div>
                                    </div>
                                  )}
                                  {error && (
                                    <div className="text-danger">
                                      {error.message}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )
                          },
                        )}
                      </div>
                      <div className="add-form-btn widget-next-btn submit-btn d-flex justify-content-end mb-0">
                        <div className="btn-left">
                          <button
                            type="submit"
                            className="btn main-btn next_btns"
                          >
                            Next <i className="isax isax-arrow-right-3 ms-1" />
                          </button>
                        </div>
                      </div>
                    </form>
                  )}
                  {currentStep === 2 && (
                    <form
                      onSubmit={handleSubmitStep2(handleNextStep)}
                      className="form-inner wizard-form-card"
                      style={{ display: 'block' }}
                    >
                      <div className="title">
                        <div className="row align-items-center row-gap-2">
                          <div className="col-md-6">
                            <h5 className="mb-0">Content</h5>
                          </div>
                          <div className="col-md-6 text-md-end">
                            <Link
                              to="#"
                              className="btn add-edit-btn d-inline-flex align-items-center"
                              onClick={handleAddTopic}
                            >
                              <i className="isax isax-add-circle5 me-1" /> Add
                              New Topic
                            </Link>
                          </div>
                        </div>
                      </div>

                      {/* Display validation error for the entire topic array if empty */}
                      {errorsStep2.topic && errorsStep2.topic.message && (
                        <div className="text-danger mb-3">
                          {errorsStep2.topic.message}
                        </div>
                      )}

                      {/* Accordion for Topics */}
                      <div
                        className="accordion accordion-flush"
                        id="topicsAccordion"
                      >
                        {topicFields.length === 0 && (
                          <div className="alert alert-info text-center mt-3">
                            Click "Add New Topic" to start adding course
                            content.
                          </div>
                        )}
                        {topicFields.map(
                          (
                            topic: FieldArrayWithId<
                              CourseContentFormValues,
                              'topic',
                              'id'
                            >,
                            topicIndex: number,
                          ) => (
                            <div
                              className="accordions-items-seperate mb-2"
                              key={topic.id}
                            >
                              <div className="accordion-item">
                                <h2
                                  className="accordion-header border-1 rounded-4"
                                  id={`topicHeading${topicIndex}`}
                                >
                                  <button
                                    className="accordion-button collapsed"
                                    type="button"
                                    data-bs-toggle="collapse"
                                    data-bs-target={`#topicCollapse${topicIndex}`}
                                    aria-expanded="false"
                                    aria-controls={`topicCollapse${topicIndex}`}
                                  >
                                    <Link
                                      to="#"
                                      className="delete-btn1"
                                      onClick={() =>
                                        handleRemoveTopic(topicIndex)
                                      }
                                      title="Remove Topic"
                                      style={{ marginRight: '10px' }}
                                    >
                                      <i className="isax isax-trash fs-16" />
                                    </Link>
                                    <span className="d-flex align-items-center mb-0 text-black">
                                      {watchStep2(`topic.${topicIndex}.name`) ||
                                        'Untitled Topic'}
                                    </span>
                                  </button>
                                </h2>
                                <div
                                  id={`topicCollapse${topicIndex}`}
                                  className="accordion-collapse collapse"
                                  aria-labelledby={`topicHeading${topicIndex}`}
                                  data-bs-parent="#topicsAccordion"
                                >
                                  <div className="accordion-body">
                                    {/* Topic Name Input */}
                                    <div className="input-block mb-3">
                                      <label className="form-label">
                                        Topic Name
                                        <span className="text-danger ms-1">
                                          *
                                        </span>
                                      </label>
                                      <input
                                        type="text"
                                        {...registerStep2(
                                          `topic.${topicIndex}.name`,
                                        )}
                                        className={`form-control ${errorsStep2.topic?.[topicIndex]?.name
                                            ? 'is-invalid'
                                            : ''
                                          }`}
                                        placeholder="Enter topic name"
                                      />
                                      {errorsStep2.topic?.[topicIndex]
                                        ?.name && (
                                          <div className="text-danger">
                                            {
                                              errorsStep2.topic[topicIndex]?.name
                                                ?.message
                                            }
                                          </div>
                                        )}
                                    </div>

                                    {/* Lessons for this Topic */}
                                    <h6 className="mt-4 mb-2">Lessons:</h6>
                                    {watchStep2(`topic.${topicIndex}.lesson`)
                                      ?.length === 0 && (
                                        <div className="alert alert-warning text-center">
                                          No lessons added to this topic. Click
                                          "Add Lesson" below.
                                        </div>
                                      )}
                                    <ul className="list-group mb-3">
                                      {watchStep2(
                                        `topic.${topicIndex}.lesson`,
                                      )?.map((lesson, lessonIndex) => (
                                        <li
                                          className="d-flex align-items-center justify-content-between bg-white p-2 border rounded-3 mb-3"
                                          key={lessonIndex}
                                        >
                                          <div className="d-flex align-items-center flex-grow-1 min-width-0">
                                            <i className="isax isax-play-circle5 text-success fs-24 me-2 flex-shrink-0" />
                                            <p className="fw-medium text-gray-5 mb-0 text-truncate">
                                              {lesson.name}
                                            </p>
                                          </div>

                                          <div className="flex-shrink-0 ms-2">
                                            <Link
                                              to="#"
                                              className="edit-btn1 me-2"
                                              onClick={() =>
                                                openLessonModal(
                                                  topicIndex,
                                                  lessonIndex,
                                                )
                                              }
                                            >
                                              <i className="isax isax-edit-25 fs-16" />
                                            </Link>

                                            <Link
                                              to="#"
                                              className="link-trash delete-btn1"
                                              onClick={() => {
                                                const currentLessons = [
                                                  ...watchStep2(
                                                    `topic.${topicIndex}.lesson`,
                                                  ),
                                                ]
                                                currentLessons.splice(
                                                  lessonIndex,
                                                  1,
                                                )
                                                setValueStep2(
                                                  `topic.${topicIndex}.lesson`,
                                                  currentLessons,
                                                  {
                                                    shouldValidate: true,
                                                    shouldDirty: true,
                                                  },
                                                )
                                              }}
                                              title="Remove Lesson"
                                            >
                                              <i className="isax isax-trash fs-16" />
                                            </Link>
                                          </div>
                                        </li>
                                      ))}
                                    </ul>

                                    {/* Add Lesson Button */}
                                    <button
                                      type="button"
                                      className="btn btn-primary btn-sm d-flex align-items-center"
                                      onClick={() =>
                                        openLessonModal(topicIndex)
                                      }
                                    // data-bs-toggle="modal" // Bootstrap 5 specific attributes
                                    // data-bs-target="#add-lesson"
                                    >
                                      <i className="isax isax-add-circle5 me-1" />{' '}
                                      Add Lesson
                                    </button>
                                    {/* Display error if no lessons for this topic */}
                                    {errorsStep2.topic?.[topicIndex]
                                      ?.lesson && (
                                        <div className="text-danger mt-2">
                                          {
                                            errorsStep2.topic[topicIndex]?.lesson
                                              ?.message
                                          }
                                        </div>
                                      )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ),
                        )}
                      </div>

                      <div className="add-form-btn widget-next-btn submit-btn mt-4">
                        <div className="btn-left">
                          <button
                            type="button"
                            className="btn btn-light main-btn prev_btns"
                            onClick={handlePrevStep}
                          >
                            <i className="isax isax-arrow-left-2 me-1" /> Prev
                          </button>
                        </div>
                        <div className="btn-left">
                          <button
                            type="submit"
                            className="btn btn-secondary main-btn next_btns"
                          >
                            Next <i className="isax isax-arrow-right-3 ms-1" />
                          </button>
                        </div>
                      </div>
                    </form>
                  )}
                  {currentStep === 3 && (
                    <form
                      onSubmit={handleSubmitStep2b(handleNextStep)}
                      className="form-inner wizard-form-card"
                      id="second-b"
                    >
                      <div className="title">
                        <h5>Question & Answer</h5>
                      </div>
                      <div className="row">
                         <div className="col-md-12">
                          <div className="input-block">
                            <div className="d-flex align-items-center gap-3">
                              <label className="form-label mb-0">
                                {courseQuestionInformationFieldsConfig.has_questions.label}
                                {courseQuestionInformationFieldsConfig.has_questions.required && (
                                  <span className="text-danger ms-1">*</span>
                                )}
                              </label>
                              <Radio.Group
                                value={watchStep2b('has_questions')}
                                onChange={(event) =>
                                  setValueStep2b(
                                    'has_questions',
                                    event.target.value,
                                    {
                                      shouldValidate: true,
                                      shouldDirty: true,
                                    },
                                  )
                                }
                              >
                                {courseQuestionInformationFieldsConfig.has_questions.options?.map(
                                  (option) => (
                                    <Radio key={option.value} value={option.value}>
                                      {option.label}
                                    </Radio>
                                  ),
                                )}
                              </Radio.Group>
                            </div>
                            {errorsStep2b.has_questions && (
                              <div className="text-danger mt-1">
                                {errorsStep2b.has_questions.message}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {isQuestionEnabled ? (
                        <div className="row">
                          <div className="col-md-12">
                            <div className="input-block">
                              <label className="form-label">
                                {courseQuestionInformationFieldsConfig.duration.label}
                                {courseQuestionInformationFieldsConfig.duration.required && (
                                  <span className="text-danger ms-1">*</span>
                                )}
                              </label>
                              <TimePicker
                                {...registerStep2b('duration')}
                                ref={registerStep2b('duration').ref}
                                className={`form-control w-100 ${
                                  errorsStep2b.duration ? 'is-invalid' : ''
                                }`}
                                placeholder={
                                  courseQuestionInformationFieldsConfig.duration.placeholder
                                }
                                value={
                                  watchStep2b('duration')
                                    ? dayjs(watchStep2b('duration') as any)
                                    : null
                                }
                                onChange={(date) =>
                                  setValueStep2b('duration', date as any, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  })
                                }
                                format="HH:mm"
                                status={
                                  errorsStep2b.duration ? 'error' : undefined
                                }
                              />
                              {errorsStep2b.duration && (
                                <div className="text-danger">
                                  {errorsStep2b.duration.message as string}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="input-block">
                              <label className="form-label">
                                {
                                  courseQuestionInformationFieldsConfig
                                    .min_passing_score.label
                                }
                                {courseQuestionInformationFieldsConfig
                                  .min_passing_score.required && (
                                  <span className="text-danger ms-1">*</span>
                                )}
                              </label>
                              <input
                                type="number"
                                {...registerStep2b('min_passing_score')}
                                value={normalizeLeadingInteger(
                                  watchStep2b('min_passing_score'),
                                )}
                                onChange={(event) =>
                                  setValueStep2b(
                                    'min_passing_score',
                                    normalizeLeadingInteger(event.target.value) as any,
                                    {
                                      shouldDirty: true,
                                      shouldValidate: true,
                                    },
                                  )
                                }
                                className={`form-control ${
                                  errorsStep2b.min_passing_score
                                    ? 'is-invalid'
                                    : ''
                                }`}
                                placeholder={
                                  courseQuestionInformationFieldsConfig
                                    .min_passing_score.placeholder
                                }
                              />
                              {errorsStep2b.min_passing_score && (
                                <div className="text-danger">
                                  {
                                    errorsStep2b.min_passing_score
                                      .message as string
                                  }
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="input-block">
                              <label className="form-label">
                                {courseQuestionInformationFieldsConfig.max_attempt.label}
                                {courseQuestionInformationFieldsConfig.max_attempt.required && (
                                  <span className="text-danger ms-1">*</span>
                                )}
                              </label>
                              <input
                                type="number"
                                {...registerStep2b('max_attempt')}
                                value={normalizeLeadingInteger(
                                  watchStep2b('max_attempt'),
                                )}
                                onChange={(event) =>
                                  setValueStep2b(
                                    'max_attempt',
                                    normalizeLeadingInteger(event.target.value) as any,
                                    {
                                      shouldDirty: true,
                                      shouldValidate: true,
                                    },
                                  )
                                }
                                className={`form-control ${
                                  errorsStep2b.max_attempt ? 'is-invalid' : ''
                                }`}
                                placeholder={
                                  courseQuestionInformationFieldsConfig.max_attempt.placeholder
                                }
                              />
                              {errorsStep2b.max_attempt && (
                                <div className="text-danger">
                                  {errorsStep2b.max_attempt.message as string}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-md-12">
                            <AddQuestionForm
                              name="questions"
                              register={registerStep2b as any}
                              errors={errorsStep2b as any}
                              setValue={setValueStep2b as any}
                              watch={watchStep2b as any}
                            />
                            {errorsStep2b.questions && (
                              <div className="text-danger mt-2">
                                {errorsStep2b.questions.message as string}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="alert alert-info mt-3">
                          Question & Answer tidak diaktifkan.
                        </div>
                      )}
                      <div className="add-form-btn widget-next-btn submit-btn">
                        <div className="btn-left">
                          <button
                            type="button"
                            className="btn btn-light main-btn prev_btns"
                            onClick={handlePrevStep}
                          >
                            <i className="isax isax-arrow-left-2 me-1" /> Prev
                          </button>
                        </div>
                        <div className="btn-left">
                          <button
                            type="submit"
                            className="btn btn-secondary main-btn next_btns"
                          >
                            Next <i className="isax isax-arrow-right-3 ms-1" />
                          </button>
                        </div>
                      </div>
                    </form>
                  )}
                  {(currentStep === 4 || currentStep === 5) && (
                    <form
                      onSubmit={
                        currentStep === 4
                          ? async (event) => {
                              event.preventDefault()
                              const isCertificateDateValid = await triggerStep3(
                                'certificate_event_dates',
                              )
                              if (!isCertificateDateValid) return
                              if (!validateCertificateFields()) return
                              setAllFormData((prev) => ({
                                ...prev,
                                ...getValuesStep3(),
                              } as CourseCreateFormValues))
                              setCurrentStep(5)
                            }
                          : handleSubmitStep3(handleNextStep)
                      }
                      className="form-inner wizard-form-card"
                      style={{ display: 'block' }}
                    >
                      <div className="title">
                        <h5>
                          {currentStep === 4
                            ? 'Sertifikat'
                            : 'Additional Information'}
                        </h5>
                      </div>
                      <div className="row">
                        {(() => {
                          const certificateFieldKeys = new Set([
                            'certificate_event_dates',
                            'certificate_jpl_total',
                            'certificate_jpl_note',
                            'certificate_materials',
                            'certificate_location',
                          ])
                          const filteredFields = Object.entries(
                            additionalInformationFieldsConfig,
                          ).filter(([key]) =>
                            currentStep === 4
                              ? certificateFieldKeys.has(key)
                              : !certificateFieldKeys.has(key),
                          )
 
                          return filteredFields.map(([key, fieldConfig]) => {
                            const error =
                              errorsStep3[
                              key as keyof CourseAdditionalInformationFormValues
                              ]
                            const isCertificateFieldRequired =
                              currentStep === 4 &&
                              isQuestionEnabled &&
                              requiredCertificateFieldKeys.includes(
                                key as CertificateRequiredField,
                              )
                            const inputClassName = `form-control ${error ? 'is-invalid' : ''
                              }`
 
                            if (
                              fieldConfig.type === 'text' ||
                              fieldConfig.type === 'url'
                            ) {
                              return (
                                <div className="col-md-12" key={key}>
                                  <div className="input-block">
                                    <label className="form-label">
                                      {fieldConfig.label}
                                      {(fieldConfig.required || isCertificateFieldRequired) && (
                                        <span className="text-danger ms-1">
                                          *
                                        </span>
                                      )}
                                    </label>
                                    <input
                                      type={
                                        fieldConfig.type === 'url'
                                          ? 'text'
                                          : fieldConfig.type
                                      }
                                      {...registerStep3(key)}
                                      className={inputClassName}
                                      placeholder={
                                        'placeholder' in fieldConfig
                                          ? fieldConfig.placeholder
                                          : undefined
                                      }
                                    />
                                    {error && (
                                      <div className="text-danger">
                                        {error.message}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )
                            }
 
                            if (fieldConfig.type === 'certificate_materials') {
                              return (
                                <div className="col-md-12" key={key}>
                                  <div className="input-block">
                                    <label className="form-label">
                                      {fieldConfig.label}
                                      {isCertificateFieldRequired && (
                                        <span className="text-danger ms-1">
                                          *
                                        </span>
                                      )}
                                    </label>
                                    <CertificateMaterialsInput
                                      value={(watchStep3(key) as string) || ''}
                                      fallbackTopics={studiedItems}
                                      onChange={(value) =>
                                        setValueStep3(key as any, value as any)
                                      }
                                    />
                                    {error && (
                                      <div className="text-danger">
                                        {error.message}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )
                            }
 
                            if (fieldConfig.type === 'textarea') {
                              return (
                                <div className="col-md-12" key={key}>
                                  <div className="input-block">
                                    <label className="form-label">
                                      {fieldConfig.label}
                                      {(fieldConfig.required || isCertificateFieldRequired) && (
                                        <span className="text-danger ms-1">
                                          *
                                        </span>
                                      )}
                                    </label>
                                    <textarea
                                      {...registerStep3(key)}
                                      className={inputClassName}
                                      rows={key === 'certificate_materials' ? 5 : 3}
                                      placeholder={
                                        'placeholder' in fieldConfig
                                          ? fieldConfig.placeholder
                                          : undefined
                                      }
                                    />
                                    {error && (
                                      <div className="text-danger">
                                        {error.message}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )
                            }

                            if (fieldConfig.type === 'array') {
                              return (
                                <div className="col-md-12 mt-4" key={key}>
                                  <div className="bg-light pt-3 rounded-3 p-3">
                                    <h6 className="mb-2">Benefits</h6>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary mb-3"
                                      onClick={addNewBenefitItem}
                                    >
                                      Add New Benefit
                                    </button>
                                    {benefitsItems.length === 0 && (
                                      <div className="alert alert-info text-center">
                                        No benefits added.
                                      </div>
                                    )}
                                    {benefitsItems.map((item, index) => {
                                      const data: any = watchStep3?.(
                                        'benefits',
                                      )?.[index]

                                      const value =
                                        additionalInformationFieldsConfig?.benefits?.options?.find(
                                          (opt) => opt.value === data?.icon,
                                        ) || null
                                      return (
                                        <div
                                          key={index}
                                          className="d-flex align-items-center mb-2"
                                        >
                                          <div className="me-2">
                                            <SelectIcon
                                              {...registerStep3(key)}
                                              style={{ width: '5rem' }}
                                              className="select"
                                              placeholder="Select Icon Benefit"
                                              value={value}
                                              onChange={(value: any) => {
                                                updateBenefitItem(
                                                  index,
                                                  'icon',
                                                  value,
                                                )
                                              }}
                                              allowClear
                                            />
                                          </div>
                                          <div className="flex-grow-1 me-2">
                                            <input
                                              type="text"
                                              className="form-control w-100"
                                              placeholder="Nama Benefit"
                                              value={item.name}
                                              onChange={(e) =>
                                                updateBenefitItem(
                                                  index,
                                                  'name',
                                                  e.target.value,
                                                )
                                              }
                                            />
                                          </div>
                                          <Link
                                            to="#"
                                            className="link-trash delete-btn1"
                                            onClick={() =>
                                              removeBenefitItem(index)
                                            }
                                          >
                                            <i className="isax isax-trash fs-16" />
                                          </Link>
                                        </div>
                                      )
                                    })}
                                    {errorsStep3.benefits && (
                                      <div className="text-danger">
                                        {errorsStep3.benefits.message}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )
                            }

                            return null
                          })
                        })()}
                      </div>
                      <div className="add-form-btn widget-next-btn submit-btn">
                        <div className="btn-left">
                          <button
                            type="button"
                            className="btn btn-light main-btn prev_btns"
                            onClick={handlePrevStep}
                          >
                            <i className="isax isax-arrow-left-2 me-1" /> Prev
                          </button>
                        </div>
                        <div className="btn-left">
                          <button
                            type="submit"
                            className="btn btn-secondary main-btn next_btns"
                          >
                            Next <i className="isax isax-arrow-right-3 ms-1" />
                          </button>
                        </div>
                      </div>
                    </form>
                  )}

                  {currentStep === 6 && (
                    <form
                      onSubmit={handleSubmitStep4(onSubmitFinal)}
                      className="form-inner wizard-form-card"
                      style={{ display: 'block' }}
                    >
                      <div className="title">
                        <h5>Pricing</h5>
                      </div>
                      <div className="row">
                        {Object.entries(pricingFieldsConfig).map(
                          ([key, fieldConfig]: any) => {
                            const error = errorsStep4[key]
                            const inputClassName = `form-control ${error ? 'is-invalid' : ''
                              }`

                            const isDiscountField =
                              key === 'discount_type' || key === 'discount'
                            const isDisabled =
                              isDiscountField && watchStep4('price') === 0

                            return (
                              <div className="col-md-12" key={key}>
                                <div className="input-block">
                                  <label className="form-label">
                                    {fieldConfig.label}
                                    {fieldConfig.required && (
                                      <span className="text-danger ms-1">
                                        *
                                      </span>
                                    )}
                                  </label>
                                  {(fieldConfig.type === 'text' ||
                                    fieldConfig.type === 'number') && (
                                      <input
                                        type={fieldConfig.type}
                                        {...registerStep4(key, {
                                          valueAsNumber:
                                            fieldConfig.type === 'number',
                                        })}
                                        className={inputClassName}
                                        placeholder={fieldConfig.placeholder}
                                        disabled={isDisabled}
                                      />
                                    )}
                                  {fieldConfig.type === 'select' && (
                                    <Select
                                      {...registerStep4(key)}
                                      options={fieldConfig?.options || []}
                                      className="select d-flex"
                                      placeholder={fieldConfig.placeholder}
                                      value={
                                        fieldConfig.options?.find(
                                          (opt: any) =>
                                            opt.value === watchStep4(key),
                                        ) || null
                                      }
                                      onChange={(selectedOption: any) =>
                                        handleSelectChange(
                                          key,
                                          selectedOption,
                                          setValueStep4,
                                        )
                                      }
                                      disabled={isDisabled}
                                    />
                                  )}

                                  {fieldConfig.type === 'array' && (
                                    <>
                                      {/* <button
                                        type="button"
                                        className="btn btn-sm btn-primary mb-3"
                                        onClick={addNewCrossSellItem}
                                      >
                                        Add New Cross-Sell Product
                                      </button> */}
                                      {crossSellItems.length === 0 && (
                                        <div className="alert alert-info text-center">
                                          No cross-sell products added.
                                        </div>
                                      )}
                                      {crossSellItems.map((item, index) => {
                                        const data: any = watchStep4?.(key)?.[
                                          index
                                        ]

                                        const value =
                                          optionsCourse?.find(
                                            (opt: any) =>
                                              opt.value?.toString() ===
                                              data?.cross_course_id?.toString(),
                                          ) || null
                                        return (
                                          <div
                                            key={index}
                                            className="d-flex align-items-center mb-2"
                                          >
                                            <div
                                              className="flex-grow-1 me-2"
                                              style={{ maxWidth: '30vw' }}
                                            >
                                              <Select
                                                {...registerStep4(key)}
                                                options={optionsCourse || []}
                                                className="select w-100"
                                                placeholder={
                                                  fieldConfig.placeholder
                                                }
                                                loading={isLoading}
                                                value={value}
                                                onChange={(
                                                  selectedOption: any,
                                                ) =>
                                                  updateCrossSellItem(
                                                    index,
                                                    'cross_course_id',
                                                    selectedOption,
                                                  )
                                                }
                                              />
                                            </div>
                                            <div className="flex-grow-1 me-2">
                                              <input
                                                type="text"
                                                className="form-control w-100"
                                                placeholder="Note (e.g., discount for this product)"
                                                value={item.note}
                                                onChange={(e) =>
                                                  updateCrossSellItem(
                                                    index,
                                                    'note',
                                                    e.target.value,
                                                  )
                                                }
                                              />
                                            </div>
                                            <div className="flex-grow-1 me-2">
                                              <input
                                                type="number"
                                                className="form-control w-100"
                                                placeholder="Price *"
                                                value={item.price || ''}
                                                onChange={(e) =>
                                                  updateCrossSellItem(
                                                    index,
                                                    'price',
                                                    e.target.value === ''
                                                      ? null
                                                      : Number(e.target.value),
                                                  )
                                                }
                                                required
                                              />
                                            </div>
                                            <Link
                                              to="#"
                                              className="link-trash delete-btn1"
                                              onClick={() =>
                                                removeCrossSellItem(index)
                                              }
                                            >
                                              <i className="isax isax-trash fs-16" />
                                            </Link>
                                          </div>
                                        )
                                      })}

                                      <div className="d-flex align-items-center justify-content-end">
                                        <Link
                                          to="#"
                                          className="d-flex align-items-center add-new-topic"
                                          id="add-new-topic-btn"
                                          onClick={addNewCrossSellItem}
                                        >
                                          <i className="isax isax-add me-1" />{' '}
                                          Add New Cross-Sell Product
                                        </Link>
                                        <div></div>
                                      </div>
                                    </>
                                  )}
                                  {error && (
                                    <div className="text-danger">
                                      {error.message}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )
                          },
                        )}
                      </div>
                      <div className="add-form-btn widget-next-btn submit-btn">
                        <div className="btn-left">
                          <button
                            type="button"
                            className="btn btn-light main-btn prev_btns"
                            onClick={handlePrevStep}
                            disabled={createMutation.isPending}
                          >
                            <i className="isax isax-arrow-left-2 me-1" /> Prev
                          </button>
                        </div>
                        <div className="btn-left">
                          <button
                            type="submit"
                            className="btn btn-success main-btn"
                            disabled={createMutation.isPending}
                          >
                            Submit Content
                          </button>
                        </div>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add topic */}
      <div className="modal fade" id="add-topic">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5>Topic Name</h5>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="isax isax-close-circle5" />
              </button>
            </div>
            <form>
              <div className="modal-body">
                <div className="input-block">
                  <label className="form-label">
                    Add Name<span className="text-danger ms-1">*</span>
                  </label>
                  <input type="text" className="form-control" />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn me-2 btn-light"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-bs-dismiss="modal"
                  className="btn btn-secondary"
                >
                  Add New
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Add topic */}
      {/* Add lesson */}
      <Modal
        show={showLessonModal}
        onHide={closeLessonModal}
        centered
        backdrop="static"
      >
        <Modal.Header>
          <Modal.Title>
            {currentLessonIndex === null ? 'Add New Lesson' : 'Edit Lesson'}
          </Modal.Title>
          <button
            type="button"
            className="btn-close custom-btn-close"
            onClick={closeLessonModal}
            aria-label="Close"
          >
            <i className="isax isax-close-circle5" />
          </button>
        </Modal.Header>
        <form onSubmit={handleLessonSubmit}>
          <Modal.Body>
            <div className="input-block mb-4">
              <label className="form-label">
                Lesson Name<span className="text-danger ms-1">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${lessonErrors.name ? 'is-invalid' : ''
                  }`}
                placeholder="Enter lesson name"
                value={lessonName}
                onChange={(e) => {
                  setLessonName(e.target.value)
                  setLessonErrors((prev) => ({ ...prev, name: undefined })) // Clear error on change
                }}
              />
              {lessonErrors.name && (
                <div className="text-danger">{lessonErrors.name}</div>
              )}
            </div>
            <div className="input-block mb-4">
              <label className="form-label">
                Video Link<span className="text-danger ms-1">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${lessonErrors.video_link ? 'is-invalid' : ''
                  }`}
                placeholder="Enter video URL"
                value={lessonVideoLink}
                onChange={(e) => {
                  setLessonVideoLink(e.target.value)
                  setLessonErrors((prev) => ({
                    ...prev,
                    video_link: undefined,
                  })) // Clear error on change
                }}
              />
              {lessonErrors.video_link && (
                <div className="text-danger">{lessonErrors.video_link}</div>
              )}
            </div>
            <div className="input-block mb-4">
              <label className="form-label">
                Duration<span className="text-danger ms-1">*</span>
              </label>
              <DurationPicker
                value={lessonDuration}
                onChange={(value) => {
                  setLessonDuration(value)
                  setLessonErrors((prev) => ({ ...prev, duration: undefined }))
                }}
                onBlur={() => {
                  if (lessonDuration && !/^\d{1,2}:\d{2}:\d{2}$/.test(lessonDuration)) {
                    setLessonErrors((prev) => ({
                      ...prev,
                      duration: 'Duration must be in HH:MM:SS format',
                    }))
                  }
                }}
                error={!!lessonErrors.duration}
                showHours
              />
              {lessonErrors.duration && (
                <div className="text-danger mt-2 mb-2">{lessonErrors.duration}</div>
              )}
            </div>
            <div className="input-block mb-4">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                placeholder="Optional lesson description"
                rows={3}
                value={lessonDescription}
                onChange={(e) => setLessonDescription(e.target.value)}
              />
            </div>
            <div className="d-flex align-items-center">
              <div className="form-check me-3">
                <input
                  className="form-check-input"
                  type="radio"
                  id="lessonRadioFree"
                  name="is_premium_radio" // Important: common name for radio group
                  checked={!isPremium}
                  onChange={() => setIsPremium(false)}
                />
                <label className="form-check-label" htmlFor="lessonRadioFree">
                  Free
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  id="lessonRadioPremium"
                  name="is_premium_radio" // Important: common name for radio group
                  checked={isPremium}
                  onChange={() => setIsPremium(true)}
                />
                <label
                  className="form-check-label"
                  htmlFor="lessonRadioPremium"
                >
                  Premium
                </label>
              </div>
            </div>
            {lessonErrors.is_premium && (
              <div className="text-danger">{lessonErrors.is_premium}</div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn me-2 btn-light"
              onClick={closeLessonModal}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-secondary">
              {currentLessonIndex === null ? 'Add Lesson' : 'Update Lesson'}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
      {/* /Add lesson */}
      {/* Add Faq */}
      <div className="modal fade" id="add-faq">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5>New FAQ</h5>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="isax isax-close-circle5" />
              </button>
            </div>
            <form>
              <div className="modal-body">
                <div className="input-block mb-4">
                  <label className="form-label">
                    Question<span className="text-danger ms-1">*</span>
                  </label>
                  <input type="text" className="form-control" />
                </div>
                <div className="input-block mb-4">
                  <label className="form-label">
                    Answer<span className="text-danger ms-1">*</span>
                  </label>
                  <textarea className="form-control" defaultValue={''} />
                </div>
                <div className="d-flex align-items-center">
                  <div className="form-check me-3">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="flexRadioDefault"
                      id="flexRadioDefault6"
                      defaultChecked
                    />
                    <label
                      className="form-check-label"
                      htmlFor="flexRadioDefault6"
                    >
                      Enable
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="flexRadioDefault"
                      id="flexRadioDefault7"
                    />
                    <label
                      className="form-check-label"
                      htmlFor="flexRadioDefault7"
                    >
                      Disable
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn me-2 btn-light"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-bs-dismiss="modal"
                  className="btn btn-secondary"
                >
                  Add New
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Add Faq */}
      {/* success */}
      <div className="modal fade modal-default" id="success">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body p-4">
              <div className="text-center">
                <div className="text-success h1 mb-2">
                  <i className="isax isax-tick-circle5" />
                </div>
                <h5 className="mb-2">Congratulations! Course Submitted</h5>
                <p className="mb-3">
                  You’ve successfully Submitted the Course &amp; its under the
                  review, Once the course is reviewed by the reviewer it will go
                  live.
                </p>
                <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                  <Link
                    to={route.instructorDashboard}
                    className="btn btn-secondary"
                  >
                    <i className="isax isax-arrow-left-2 me-1" />
                    Back to Dashboard
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /success */}
    </>
  )
}

export default CourseCreate
